import { ChevronRight, Folder } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useData, useMutation, useServerErrors } from '../../../Shared/api';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Project } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import InfoMessage from '../../../Shared/UI/InfoMessage';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { Spinner } from '../../../Shared/UI/Spinner';
import { useRouter } from '../../router';

export default function AddCustomMailForm({
  project,
  projects,
  onSuccess,
}: {
  project: Project;
  projects: PickerItem[];
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      project_id: project.id,
      form_type: 'add' as 'add' | 'import',
      import_project_id: '',
      import_mails: [] as string[],
      title: '',
    },
  });

  const {
    register, handleSubmit, setError, setValue, watch, control,
    formState: { errors, defaultValues },
  } = form;

  const formType = watch('form_type');
  const importMailIds = watch('import_mails');
  const importProjectId = watch('import_project_id');

  const [mails, { loading: loadingMails }] = useData<PickerItem[]>(
    importProjectId ? routes.project_mails_json(importProjectId) : null,
  );

  const toggleImportMails = () => {
    if (!loadingMails && mails) {
      setValue('import_mails', importMailIds.length > 0 ? [] : mails?.map((importMail) => importMail.id));
    }
  };

  const [addOrImport, { loading, errors: serverErrors }] = useMutation(
    formType === 'add' ? routes.add_mail() : routes.import_mails(),
    {
      onSuccess: (result) => {
        const successType = result.component.includes('EditCustomMailPage') ? 'add' : 'import';
        notifier.notify(successType === 'add' ? t('backend:mail_created') : t('backend:mails_imported'));
        onSuccess?.();
      },
      onError: (errors) => {
        setServerErrors(errors, setError, defaultValues);
      },
    },
  );

  useServerErrors(form, serverErrors);

  const submit = async (data: typeof defaultValues) => {
    if (data?.form_type === 'add') {
      addOrImport({
        project_id: data?.project_id,
        title: data?.title,
      });
    } else if (data?.form_type === 'import') {
      addOrImport({
        project_id: data?.project_id,
        import_mails: data?.import_mails,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_mail')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_mail_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel>
            {t('backend:add_mail')}
          </InputLabel>
          <RadioLabel>
            <Radio
              {...register('form_type', { required: true })}
              value="add"
              aria-invalid={!!errors.form_type}
            />
            <span>
              {t('backend:add_new_mail')}
            </span>
          </RadioLabel>
          <RadioLabel>
            <Radio
              {...register('form_type', { required: true })}
              value="import"
              aria-invalid={!!errors.form_type}
            />
            <span>
              {t('backend:import_existing_mails')}
            </span>
          </RadioLabel>
        </InputGroup>

        {formType === 'add' && (
          <InputGroup>
            <InputLabel required valid={!errors.title} htmlFor="add_mail_title">
              {t('backend:mail_name')}
            </InputLabel>
            <InputDescription>
              {t('backend:mail_name_description')}
            </InputDescription>
            <Input
              {...register('title', { required: true })}
              id="add_mail_title"
              className="mr-4"
              aria-invalid={!!errors.title}
            />
            <ErrorMessage attribute={t('backend:mail_name')} error={errors.title }/>
          </InputGroup>
        )}

        {formType === 'import' && (
          <>
            <InputGroup>
              <InputLabel required valid={!errors.import_project_id} htmlFor="mail_import_project">
                {t('backend:import_from_project')}
              </InputLabel>
              <InputDescription>
                {t('backend:import_from_project_description')}
              </InputDescription>
              <Controller
                control={control}
                name="import_project_id"
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Picker
                    selected={value}
                    items={projects}
                    onChange={(value) => {
                      onChange(value?.id || null);
                      setValue('import_mails', []);
                    }}
                    onBlur={onBlur}
                    icon={<Folder />}
                    aria-invalid={!!errors.import_project_id}
                    id="mail_import_project"
                  />
                )}
              />
              <ErrorMessage attribute={t('backend:project')} error={errors.import_project_id} />
            </InputGroup>

            {importProjectId && (
              <>
                <InputGroup>
                  <InputLabel required valid={!errors.import_mails} htmlFor={`mail_import_mails_${mails?.[0]?.id}`}>
                    {t('backend:mails_to_import')}
                  </InputLabel>
                  <InputDescription>
                    {t('backend:mails_to_import_description')}
                  </InputDescription>
                  {!loadingMails && mails && mails.length > 0 && (
                    <div className="mb-2">
                      <a onClick={() => toggleImportMails()} role="button">
                        {importMailIds.length > 0 ? t('backend:deselect_all') : t('backend:select_all')}
                      </a>
                    </div>
                  )}
                  {!loadingMails && mails?.map((importMail) => (
                    <CheckboxLabel htmlFor={`import_mail_${importMail.id}`} key={importMail.id}>
                      <Checkbox
                        {...register('import_mails', { required: true })}
                        value={importMail.id}
                        id={`import_mail_${importMail.id}`}
                        className="mt-1"
                      />
                      <span>
                        {importMail.title}
                      </span>
                    </CheckboxLabel>
                  ))}
                  {loadingMails && <Spinner />}
                  <ErrorMessage attribute={t('backend:mails_to_import')} error={errors.import_mails} />
                </InputGroup>

                {!loadingMails && mails && mails.length === 0 && (
                  <InfoMessage variant="alert">
                    {t('backend:no_mails_to_import')}
                  </InfoMessage>
                )}
              </>
            )}
          </>
        )}

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading || (formType === 'import' && !!mails && mails.length === 0)}
            loading={loading}
          >
            {formType === 'add' ? t('backend:continue') : t('backend:import')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
